import { AxiosResponse } from 'axios';
import { CodeEditorTheme } from '@core/classes/CodeEditorTheme';
import { DealKind } from '@core/classes/SharedEnums';
import { getSaturnGetRequest } from './_api';
import { IPaperworkDocumentConfig } from '@core/classes/Paperwork/IPaperworkDocumentConfig';
import request from '@core/services/request';

export const utilitiesAPI = {
	getHelpData: (getHelpDataRequest: any) => {
		return request.post(`/Utilities/GetHelpData`, getHelpDataRequest);
	},
	sendHelp: (helpRequest: any) => {
		return request.postSpecial(`/Utilities/SendHelp`, helpRequest);
	},
	sendHelpUserRequest: (helpUserRequest: any) => {
		return request.post(`/Utilities/SendHelpUserRequest`, helpUserRequest);
	},
	sendHelpLenderRequest: (helpLenderRequest: any) => {
		return request.post(`/Utilities/SendHelpLenderRequest`, helpLenderRequest);
	},
	sendHelpFormRequest: (helpFormRequest: any) => {
		return request.postSpecial(`/Utilities/SendHelpFormRequest`, helpFormRequest);
	},
	sendHelpReply: (helpReplyRequest: any) => {
		return request.postSpecial(`/Utilities/SendHelpReply`, helpReplyRequest);
	},
	getHelpHistory: (helpHistoryRequest: any) => {
		return request.post(`/Utilities/GetHelpHistory`, helpHistoryRequest);
	},
	sendMessage: (messageRequest: any) => {
		return request.post(`/Utilities/SendMessage`, messageRequest);
	},
	sendEmail: (emailRequest: any) => {
		return request.post('/Utilities/SendEmail', emailRequest);
	},
	encrypt: (value: any) => {
		return request.post('/Utilities/Encrypt', { value });
	},
	decrypt: (value: any) => request.get(`/Utilities/Decrypt?value=${value}`),
	//Copy from prod
	copyDealFromProd: (dealNumber: any, dealKind = DealKind.Deal) => {
		return request.post(`/Utilities/CopyDealFromProd?dealNumber=${dealNumber}&dk=${dealKind}`);
	},
	copySettingsFromProd: (fields: any) => {
		return request.post(`/Utilities/CopySettingsFromProd`, fields);
	},
	copyEmployeesFromProd: () => {
		return request.post(`/Utilities/CopyEmployeesFromProd`);
	},
	copyStoresFromProd: () => {
		return request.post(`/Utilities/CopyStoresFromProd`);
	},
	copyProductsFromProd: () => {
		return request.post(`/Utilities/CopyProductsFromProd`);
	},
	copyCustomerFromProd: (customerId: any) => {
		return request.post(`/Utilities/CopyCustomerFromProd?customerId=${customerId}`);
	},
	copyLendersFromProd: () => {
		return request.post(`/Utilities/CopyLendersFromProd`);
	},
	copyLegalTermsFromProd: () => {
		return request.post(`/Utilities/CopyLegalTermsFromProd`);
	},
	copyPermissionSettingsFromProd: () => {
		return request.get(`/Utilities/CopyPermissionSettingsFromProd`);
	},
	copyChecklistItemsFromProd: () => {
		return request.get(`/Utilities/CopyChecklistItemsFromProd`);
	},
	//Error logs
	getErrorLogs: (path: any, source: any) => {
		const logType = source === 'stream' ? 'GetStreamLogs' : 'GetErrorLogs';
		const apiGetFunction = source === 'media' || source === 'stream' ? request.getMs : request.get;
		return apiGetFunction(`/Utilities/${logType}?path=${path}`);
	},
	getLogFile: (path: any, source: any) => {
		const logType = source === 'stream' ? 'GetStreamLogFile' : 'GetLogFile';
		const apiGetFunction = source === 'media' || source === 'stream' ? request.getMs : request.get;
		return apiGetFunction(`/Utilities/${logType}?path=${path}`);
	},
	getWebhookLogsPaged: (pagedDataTable: any) => request.post(`/Utilities/GetWebhookLogsPaged`, pagedDataTable),
	getWebhookLogPayloadBytes: (webhookLogId: any) => request.get(`/Utilities/GetWebhookLogPayloadBytes?id=${webhookLogId}`),
	downloadWebhookLogPayload: (webhookLogId: any, filename: any) => request.download(`/Utilities/DownloadWebhookLogPayload?id=${webhookLogId}`, filename),
	// Custom Variables
	recoverCustomVariableFromProd: (id: any) => {
		return request.post(`/Utilities/RecoverCustomVariableFromProd/${id}`);
	},
	pushCustomVariableToProd: (id: any) => {
		return request.post(`/Utilities/PushCustomVariableToProd/${id}`);
	},
	//Base paperwork
	getNewDocumentFields: (form: FormData): Promise<AxiosResponse<string[]>> => {
		return request.post(`/Utilities/GetNewDocumentFields`, form);
	},
	downloadBasePaperworkPDFDoc: (pdfName: any) => {
		return request.download(`/Utilities/DownloadBasePaperworkPDFDoc/${pdfName}`, pdfName);
	},
	//Base paperwork new routes
	getPaperworkCategories: () => {
		return request.get(`/Utilities/GetPaperworkCategories`);
	},
	savePaperworkCategory: (categoryId: any) => {
		return request.post(`/Utilities/SavePaperworkCategory`, categoryId);
	},
	changeCategoryOrder: (CategoryIds: any) => {
		return request.post(`/Utilities/ChangeCategoryOrder`, CategoryIds);
	},
	deletePaperworkCategory: (categoryId: any) => {
		return request.post(`/Utilities/DeletePaperworkCategory/${categoryId}`);
	},
	pushCategoriesToProduction: () => {
		return request.post(`/Utilities/PushCategoriesToProduction`);
	},
	getDocConfigsByIds: (ids: any, overridesModel?: any, modelType?: any) => {
		return request.post(`/Utilities/GetDocConfigsByIds`, getSaturnGetRequest(ids, overridesModel, modelType));
	},
	getDocConfigsByCodes: (codes: any, overridesModel?: any, modelType?: any) => {
		return request.post(`/Utilities/GetLatestDocConfigsByCodes`, getSaturnGetRequest(codes, overridesModel, modelType));
	},
	getDocConfigs: (): Promise<AxiosResponse<IPaperworkDocumentConfig[]>> => {
		return request.get(`/Utilities/GetDocConfigs`);
	},
	getActiveDocConfigForCode: (id: any) => {
		return request.get(`/Utilities/GetActiveDocConfigForCode?id=${id}`);
	},
	getDocConfigsByCode: (code: string, fromProd: boolean): Promise<AxiosResponse<IPaperworkDocumentConfig[]>> => {
		return request.post(`/Utilities/GetDocConfigsByCode`, { code, fromProd });
	},
	saveDocConfig: (docConfig: any, saveNewConfig: any) => {
		return request.post(`/Utilities/SaveDocConfig?saveNewConfig=${saveNewConfig}`, docConfig);
	},
	saveDocConfigAndPDF: (form: any) => {
		return request.post(`/Utilities/SaveDocConfigAndPDF`, form);
	},
	recoverDocConfigAndPDFFromProd: (id: any) => {
		return request.post(`/Utilities/RecoverDocConfigAndPDFFromProd/${id}`);
	},
	pushDocConfigAndPDFToProd: (id: any) => {
		return request.post(`/Utilities/PushDocConfigAndPDFToProd/${id}`);
	},
	deleteDocConfigAndPDF: (id: any) => {
		return request.post(`/Utilities/DeleteDocConfigAndPDF/${id}`);
	},
	//
	evaluateExpression: (
		model: any,
		modelType: any,
		formula: any,
		varName: any,
		skipCache = false,
		additionalFormulas: any = null,
		additionalVariables: any = null,
		loadDealFromProd = false,
		loadModelAsDynamic = false, //THIS WILL TELL THE API TO TRY TO USE THE MODEL AS IT IS PASSED UP INSTEAD OF CASTING THE MODEL AS modelType
		//HOWEVER, the modelType is still used as as the baseModel
		// IF ANY PROPERTIES OF THE MODEL ARE THE SAME AS THE BASE DATA MODEL, THEN THE API WILL USE THE BASE DATA MODEL FOR THOSE PROPERTIES.
	) =>
		request.post(`/Utilities/ExpressionEvaluator`, {
			model,
			modelType,
			formula,
			varName,
			skipCache,
			additionalFormulas,
			additionalVariables,
			loadDealFromProd,
			loadModelAsDynamic,
		}),
	evaluateExpressions: (testModel: any, modelType: any, listOfExpressions: any, skipCache = false) =>
		request.post(`/Utilities/ExpressionsEvaluator`, {
			testModel,
			modelType,
			listOfExpressions,
			skipCache,
		}),
	getReserveProfits: (model: any, splitFormula: any, flatFormula: any) =>
		request.post(`/Utilities/GetReserveProfits`, {
			flatFormula,
			splitFormula,
			model,
		}),
	evaluateExpressionTest: (listOfExpressions: any, testModel: any, modelType: any) =>
		request.post(`/Utilities/ExpressionEvaluatorTest`, {
			listOfExpressions,
			testModel,
			modelType,
		}),
	codeAutocomplete: (codeAutocompleteRequest: any) => request.post(`/Utilities/CodeAutocomplete`, codeAutocompleteRequest),
	getCarCalcsTitleIds: () => request.get(`/Lookup/GetCarCalcsTitleIds`),
	getFileGeneral: (directoryPath: any, fileName: any, filetype: any) =>
		request.get(`/Utilities/GetFileGeneral?directoryPath=${directoryPath}&fileName=${fileName}&filetype=${filetype}`, { headers: { Accept: 'image/png' } }),
	getFileFiMenu: (directoryPath: any, fileName: any, filetype: any) =>
		request.get(`/Utilities/GetFileFiMenu?directoryPath=${directoryPath}&fileName=${fileName}&filetype=${filetype}`, { headers: { Accept: 'image/png' } }),
	downloadRichTable: (downloadRequest: any, fileName: any) => request.downloadPost(`/Utilities/DownloadRichTable`, downloadRequest, fileName),
	downloadRichTableFIMenu: (downloadRequest: any, fileName: any) => request.downloadPost(`/Utilities/DownloadRichTable/FIMenu`, downloadRequest, fileName),
	downloadRichTableCustomers: (downloadRequest: any, fileName: any) => request.downloadPost(`/Utilities/DownloadRichTable/Customers`, downloadRequest, fileName),
	downloadRichTableEmployees: (downloadRequest: any, fileName: any) => request.downloadPost(`/Utilities/DownloadRichTable/Employees`, downloadRequest, fileName),
	getDeal: (dealNumber: any) => request.get(`/Utilities/GetDeal?dealNumber=${dealNumber}`),
	themes: {
		upsertCustomTheme: (theme: CodeEditorTheme) => request.post('/Utilities/UpsertTheme', theme),
		deleteCustomTheme: (theme: CodeEditorTheme) => request.post('/Utilities/DeleteTheme', theme),
		getAllCustomThemes: () => request.get('/Utilities/GetAllThemes'),
	},
};
